"use client";

import { SITE_CONFIG } from "../../lib/site-config";

export default function RefundClient() {
  return (
    <main className="max-w-5xl mx-auto px-4 py-14">
      {/* ================= HEADER ================= */}
      <div className="mb-14 text-center">
        <h1 className="text-4xl font-semibold tracking-tight">
          Refund & Return Policy
        </h1>
        <p className="text-sm text-gray-500 mt-3">
          Last updated: 24 December 2025
        </p>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="space-y-8">
        {/* INTRO */}
        <section className="bg-white border rounded-2xl p-8 text-sm text-gray-700 leading-relaxed">
          <p>
            This Refund & Return Policy outlines the terms and conditions under
            which products purchased from{" "}
            <strong>{SITE_CONFIG.name}</strong> (online or from our
            physical store) may be returned, exchanged, or refunded.
          </p>
        </section>

        <PolicyCard title="1. Eligibility for Returns">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Products must be returned within <strong>7 days</strong> of
              delivery or purchase.
            </li>
            <li>
              Items must be unused, undamaged, and in original packaging with
              all accessories.
            </li>
            <li>Original invoice or proof of purchase is required.</li>
          </ul>
        </PolicyCard>

        <PolicyCard title="2. Non-Returnable Items">
          <ul className="list-disc list-inside space-y-1">
            <li>Software, digital licenses, or activation keys</li>
            <li>Customized or special-order products</li>
            <li>Products damaged due to misuse or negligence</li>
            <li>Items without original packaging or missing accessories</li>
          </ul>
        </PolicyCard>

        <PolicyCard title="3. Refund Conditions">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Refunds are issued only after product inspection and approval.
            </li>
            <li>Shipping and delivery charges are non-refundable.</li>
            <li>
              Refunds will be processed using the original payment method where
              possible.
            </li>
            <li>
              Processing time may take <strong>7–10 business days</strong>.
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="4. Exchange Policy">
          <p>
            Exchanges are allowed for defective or incorrect items, subject to
            availability. If a replacement is unavailable, a refund or store
            credit may be offered.
          </p>
        </PolicyCard>

        <PolicyCard title="5. Damaged or Defective Products">
          <p>
            If you receive a damaged or defective product, please notify us
            within <strong>48 hours</strong> of delivery with clear photos or
            video evidence. We will arrange a replacement or refund after
            verification.
          </p>
        </PolicyCard>

        <PolicyCard title="6. In-Store Purchases">
          <p>
            Products purchased from our physical store are eligible for return
            or exchange under the same conditions stated above. Inspection will
            be performed at the store before approval.
          </p>
        </PolicyCard>

        <PolicyCard title="7. Warranty vs Returns">
          <p>
            After the return window expires, product issues will be handled
            under the manufacturer’s warranty terms. Warranty claims do not
            qualify for refunds unless explicitly stated.
          </p>
        </PolicyCard>

        <PolicyCard title="8. How to Request a Return">
          <ul className="list-disc list-inside space-y-1">
            <li>Contact our support team via phone or email</li>
            <li>Provide order number or invoice details</li>
            <li>Describe the issue clearly with supporting evidence</li>
          </ul>
        </PolicyCard>

        <PolicyCard title="9. Policy Updates">
          <p>
            We reserve the right to modify this Refund & Return Policy at any
            time. Changes will be effective immediately upon posting.
          </p>
        </PolicyCard>

        {/* CONTACT SECTION */}
        <section className="border rounded-2xl p-8 bg-gray-50">
          <h2 className="text-lg font-semibold mb-3">Contact Us</h2>
          <p className="text-sm text-gray-700">
            For return or refund-related queries, please contact:
          </p>

          <p className="mt-4 text-sm leading-relaxed">
            <strong>{SITE_CONFIG.name}</strong>
            <br />
            Email: {SITE_CONFIG.email}
            <br />
            Phone: {SITE_CONFIG.phone}
            <br />
            Address: {SITE_CONFIG.address}
          </p>
        </section>
      </div>
    </main>
  );
}

/* ================= REUSABLE CARD ================= */

function PolicyCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border rounded-2xl p-8 space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}